import React from 'react';

import { type EventExtraData, InvalidStateError, isDefined, isEmpty, padStringToFixed, silenceError } from '@nfctron/nexus-core';
import { UiButton, useContextRequired, useFormat, useIsMounted, useToast } from '@nfctron/nexus-react';
import { ConfirmationIllustration, InfoSquareIcon } from '@nfctron/nexus-react-svg';

import { Alert, Loader, Text } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInSeconds } from 'date-fns';
import { ClockIcon, LockIcon, TicketIcon, TriangleAlertIcon } from 'lucide-react';
import Countdown from 'react-countdown';
import { z } from 'zod';

import { useRouterParams } from '@root/config/src/lib/hooks/useRouterParams';

import { TextInput } from '@root/features/form/components/TextInput/TextInput';

import { ImportedTicketCard } from '@root/cart/cart/_resources/components/Ticket/ImportedTicketCard';

import { QrCodeReaderButton } from '@root/lib/components/QrCodeReaderButton/QrCodeReaderButton';
import { Trans } from '@root/lib/components/Trans/Trans';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useNowState } from '@root/lib/hooks/useNow';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { EventCartPageContext } from '../../components/CartContext';
import {
	CART_VIEW,
	cartStoreQueryKeys,
	cartStoreSelectors,
	getSaleStatus,
	useCartStore,
	useTicketImportApi,
	useTicketTypesApiQuery,
} from '../../stores/cartStore';
import { getTranslationWithFallback } from '@root/config/src/lib/utils/translation';

// todo replace with actual data from the API
const extraData = {
	cs: {
		ticketCodeLabel: 'Kód vstupenky',
		ticketCodeDescription: 'Opište kód, který je umístěn pod QR kódem, který je na vaší vytištěné vstupence. Poté si budete moci dobít kredit.',
		ticketCodePlaceholder: 'Kód naleznete na vaší vstupence',
	},
	en: {
		ticketCodeLabel: 'Ticket code',
		ticketCodeDescription:
			'Print the code that is placed under the QR code that is on your printed ticket. You will then be able to top up your credit.',
		ticketCodePlaceholder: 'The code can be found on your ticket',
	},
	de: {
		ticketCodeLabel: 'Ticket-Code',
		ticketCodeDescription:
			'Drucken Sie den Code aus, der sich unter dem QR-Code auf Ihrem ausgedruckten Ticket befindet. Sie können dann Ihr Guthaben aufladen.',
		ticketCodePlaceholder: 'Der Code befindet sich auf Ihrem Ticket',
	},
	sk: {
		ticketCodeLabel: 'Kód vstupenky',
		ticketCodeDescription: 'Zadajte kód, ktorý je, ktorý je umiestnený pod kódom QR na vytlačenom lístku. Potom si budete môcť dobiť kredit.',
		ticketCodePlaceholder: 'Kód nájdete na vstupenke',
	},
};

// Define language schema with fallback
const langSchema = z.enum(['cs', 'en', 'de', 'sk']).catch('cs');

/**
 * Event/Cart/TicketsViewWithImportNfctronTicketsContent atom component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const TicketsViewWithImportNfctronTicketsContentAtom: AtomComponent<{
	nfctronTicketsConfig: NonNullable<EventExtraData['nfctronImportProvider']>;
}> = ({ nfctronTicketsConfig }) => {
	const { event, state } = useContextRequired(EventCartPageContext);
	const { t, lang } = useTranslate(APP_I18N_NAMESPACE.MAIN, TicketsViewWithImportNfctronTicketsContentAtom.displayName);

	const validatedLang = langSchema.parse(lang);

	// Helper function to get translations with fallback
	const getT = React.useCallback((key: string) => getTranslationWithFallback(extraData, validatedLang, key, t), [validatedLang, t]);

	const format = useFormat();
	const isMounted = useIsMounted();
	const ticketRegExp = !isEmpty(nfctronTicketsConfig.regex) ? new RegExp(nfctronTicketsConfig.regex) : /^[a-zA-Z0-9]{20}$/;
	const queryClient = useQueryClient();
	const setCartView = useCartStore((store) => store.setCartView);
	const importTicketToCart = useCartStore((store) => store.importTicketToCart);
	const toast = useToast();

	/** return null to prevent mixing inserted/imported and normal tickets */
	const willGetMixed = useCartStore((store) => store.cartedTickets.length > 0 && !cartStoreSelectors.hasSomeImportedCartedTicket(store));

	/** cart tickets with import for nfctronTickets integration */
	const { ticketCode, isTicketCodeValid, setTicketCode, ticketCodeApi, ticketCodeMutation } = useTicketImportApi({
		isPartner: true,
		validate(code) {
			return ticketRegExp.test(code);
		},
	});

	const ticketTypesApiQuery = useTicketTypesApiQuery();

	/** auto import ticket with topupAmount from params */
	const urlSearchParams = useRouterParams();
	const importTicketCodeParam = urlSearchParams.get('importTicketCode')?.toString();
	const importTicketTopupAmount = silenceError(() => Number.parseInt(urlSearchParams.get('topupAmount')?.toString() ?? '', 10) * 100, null);
	React.useEffect(() => {
		if (!isEmpty(importTicketCodeParam)) {
			ticketCodeMutation
				.mutateAsync({ ticketCode: importTicketCodeParam })
				.then((importedTicketType) => {
					if (importedTicketType.isDuplicate) throw new InvalidStateError('ticket is duplicate');

					/** import the ticket */
					importTicketToCart({
						ticket: importedTicketType.ticket,
						ticketType: importedTicketType.ticketType,
						topupAmount: importTicketTopupAmount ?? 0,
					});

					setCartView(CART_VIEW.CART);
				})
				.catch(() => {
					toast.warning('load');
				});
		}
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [importTicketCodeParam, importTicketTopupAmount]);

	/** get import date stata */
	const [{ shouldHaveTimer, timerInterval }] = React.useState(() => {
		const timeState = getSaleStatus({ saleFrom: nfctronTicketsConfig.activeFrom, saleTo: nfctronTicketsConfig.activeTo, defaultStatus: 'not-set' });
		switch (timeState) {
			case 'active': {
				const secondsUntilEnd = Math.abs(differenceInSeconds(new Date(nfctronTicketsConfig.activeTo), new Date()));
				return { shouldHaveTimer: true, timerInterval: secondsUntilEnd > 30 ? 60 : 1 };
			}
			case 'not-started': {
				const secondsUntilStart = Math.abs(differenceInSeconds(new Date(nfctronTicketsConfig.activeFrom), new Date()));
				return { shouldHaveTimer: true, timerInterval: secondsUntilStart > 30 ? 60 : 1 };
			}
			default: {
				return { shouldHaveTimer: false, timerInterval: 1000 };
			}
		}
	});
	const now = useNowState({ interval: timerInterval, autoStart: shouldHaveTimer });
	const importState = React.useMemo(() => {
		return getSaleStatus({ saleFrom: nfctronTicketsConfig.activeFrom, saleTo: nfctronTicketsConfig.activeTo, defaultStatus: 'not-set', now });
	}, [nfctronTicketsConfig.activeFrom, nfctronTicketsConfig.activeTo, now]);

	/** hide when mixed with sale mode if not allowed */
	if (state.isTicketSaleEnabled && !nfctronTicketsConfig.allowedWithSale) return null;
	/** hide when mixed with sale mode, as it will only distract */
	if (state.isTicketSaleEnabled && importState === 'ended') return null;
	if (state.isTicketSaleEnabled && importState === 'not-started') return null;

	return (
		<section className="flex flex-col gap-2">
			{/* heading */}
			<span className="text-lg font-bold text-zinc-700">{t('heading')}</span>

			{/* card */}
			<div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
				{/* not-started alert */}
				{importState === 'not-started' && (
					<Alert icon={<ClockIcon className="size-4" />} title={t('not-startedAlertTitle')} color="violet">
						<div className="flex flex-col gap-2">
							{/* text */}
							<span className="text-md">
								<Trans
									tFunc={t}
									i18nKey={event.eventId === '3496e974-f0d8-46e1-a4a6-073cd59897cc' ? 'not-startedTextWithImport' : 'not-startedText'}
									values={{
										dateFormatted: format.date(nfctronTicketsConfig.activeFrom, 'd. MMMM yyyy').replace(/\s/g, ' '),
										timeFormatted: format.date(nfctronTicketsConfig.activeFrom, 'HH:mm'),
									}}
									components={{
										html_bold: <b className="text-violet-500" />,
									}}
								/>
							</span>
						</div>
					</Alert>
				)}

				{/* ended alert */}
				{importState === 'ended' && (
					<Alert icon={<InfoSquareIcon className="size-4" />} title={t('importEndedTitle')} color="violet">
						{t('importEndedText')}
					</Alert>
				)}

				{/* active/not-started state */}
				{importState !== 'ended' && (
					<React.Fragment>
						{/* ticket code input */}
						<TextInput
							value={ticketCode}
							onChange={(e) => setTicketCode(e.currentTarget.value)}
							placeholder={willGetMixed ? t('ticketCodeWillGetMixedPlaceholder') : getT('ticketCodePlaceholder')}
							maxLength={36}
							disabled={importState === 'not-started' || willGetMixed || ticketCodeApi.isLoading || ticketTypesApiQuery.isLoading}
							label={getT('ticketCodeLabel')}
							description={getT('ticketCodeDescription')}
							error={isTicketCodeValid || isEmpty(ticketCode) ? undefined : t('ticketCodeErrorLabel')}
							leftSection={importState === 'not-started' || willGetMixed ? <LockIcon size={16} /> : <TicketIcon size={16} />}
							classNames={{
								wrapper: 'mt-0! lg:grow',
							}}
							inputContainer={(children) => (
								<React.Fragment>
									{/* not-started */}
									{importState === 'not-started' && (
										<Countdown
											date={nfctronTicketsConfig.activeFrom}
											renderer={({ hours, minutes, seconds, api, days, props }) => {
												if (!isMounted) return null;
												if (api.isCompleted()) return null;

												return (
													<div className="mt-1 flex items-center justify-center rounded-lg border border-gray-200/50 bg-gray-100/75 p-2">
														{/* content */}
														<div className="flex flex-col gap-0.5">
															{/* labels */}
															<div className="text-2xs flex items-center justify-center gap-1 text-center leading-none text-gray-500 uppercase">
																<span className="w-12">{t('daysLabel')}</span>
																<span className="w-12">{t('hoursLabel')}</span>
																<span className="w-12">{t('minutesLabel')}</span>
																<span className="w-12">{t('secondsLabel')}</span>
															</div>
															{/* values */}
															<div className="flex items-center justify-center">
																{[days, hours, minutes, seconds].map((value, index, arr) => (
																	<React.Fragment key={index}>
																		<div className="w-12 text-center text-2xl font-bold text-gray-700">
																			{padStringToFixed(value.toString(), 2, '0')}
																		</div>
																		{index !== arr.length - 1 && <span className="font-bold text-gray-400">:</span>}
																	</React.Fragment>
																))}
															</div>
														</div>
													</div>
												);
											}}
										/>
									)}

									<div className="mt-2 flex flex-col gap-2 lg:flex-row lg:items-center">
										{/* open QR code scanner */}
										<QrCodeReaderButton
											onScanned={(result) => setTicketCode(result.qrCode)}
											className="min-h-10 lg:order-1"
											disabled={importState === 'not-started' || willGetMixed || ticketCodeApi.isLoading || ticketTypesApiQuery.isLoading}
										>
											{t('scanQrCodeButton')}
										</QrCodeReaderButton>

										{children}
									</div>
								</React.Fragment>
							)}
						/>

						{/* response */}
						{ticketCodeApi.isSuccess && isDefined(ticketCodeApi.data) && !willGetMixed ? (
							/** ticket code API success */
							<ImportedTicketCard ticketType={ticketCodeApi.data.ticketType} importedTicket={ticketCodeApi.data.ticket}>
								{/* not topupable ticket */}
								{!ticketCodeApi.data.isTopupable && (
									<Alert className="mb-3" title={t('ticketNotTopupableLabel')} color="orange">
										{t('ticketNotTopupableText')}
									</Alert>
								)}
								<UiButton
									fullWidth
									variant="primary"
									disabled={ticketCodeApi.data.isDuplicate || !ticketCodeApi.data.isTopupable}
									onClick={async () => {
										if (!isDefined(ticketCodeApi.data)) throw new InvalidStateError('data is not defined');
										if (ticketCodeApi.data.isDuplicate) throw new InvalidStateError('ticket is duplicate');

										/** import the ticket */
										importTicketToCart({ ticket: ticketCodeApi.data.ticket, ticketType: ticketCodeApi.data.ticketType, topupAmount: 0 });

										/** open feedback confirm modal */
										openConfirmModal({
											title: t('importedTicketConfirmTitle'),
											children: (
												<div>
													{/* illustration */}
													<ConfirmationIllustration className="h-[250px] w-full" />
													{/* text */}
													<p>{t('importedTicketConfirmText')}</p>
												</div>
											),
											labels: {
												confirm: t('importedTicketConfirmButton'),
												cancel: t('importedTicketCancelButton'),
											},
											onConfirm() {
												setCartView(CART_VIEW.CART);
											},
											onClose() {
												setTicketCode('');
												queryClient.removeQueries({ queryKey: cartStoreQueryKeys.getTicketImportApiQueryKey(event.eventId, ticketCode) });
											},
										});
									}}
								>
									{ticketCodeApi.data.isDuplicate ? t('importTicketButtonDuplicateLabel') : t('importTicketButtonLabel')}
								</UiButton>
							</ImportedTicketCard>
						) : ticketCodeApi.isError ? (
							<React.Fragment>
								{/* ticket code API error */}
								{(ticketCodeApi.error as any)?.name === 'ticket-type-not-available' ? (
									<Alert icon={<TriangleAlertIcon className="size-4" />} title={t('ticketCodeSaleErrorTitle')} color="orange">
										{t('ticketCodeSaleErrorDescription')}
									</Alert>
								) : (
									<Alert icon={<TriangleAlertIcon className="size-4" />} title={t('ticketCodeErrorTitle')} color="orange">
										{t('ticketCodeErrorDescription')}
									</Alert>
								)}
							</React.Fragment>
						) : (
							ticketCodeApi.isLoading && (
								/** ticket code API loading */
								<div className="grid min-h-[75px] w-full place-items-center rounded-lg bg-gray-50 p-3">
									<Loader variant="dots" />
								</div>
							)
						)}
					</React.Fragment>
				)}
			</div>
		</section>
	);
};

/**
 * Event/Cart/TicketsViewWithImportNfctronTicketsContentAtom atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
TicketsViewWithImportNfctronTicketsContentAtom.displayName = 'Event/Cart/TicketsViewWithImportNfctronTicketsContentAtom';
